import type { StyleSystemProps } from '@withjoy/joykit';
import type { ResolvedMasthead } from '@shared/types/Masthead.types';

// Fixed offsets nudge the names relative to the art; the assets are designed with
// negative space, so name/art overlap is intentional. Both the offsets and the painted
// art size halve below sm2 (768px).
const TOP_ORNAMENT_PADDING_TOP = { _: 80, sm2: 160 };
const CENTER_FRAME_PADDING_Y = { _: 0, sm2: 50 };
// Vertical breathing room between the name and the frame art's inner edges.
export const MASTHEAD_TITLE_PADDING_Y = 30;

/**
 * Minted-style art placement, split across two elements so the art can bleed to the
 * viewport while the band content stays inside the 1024px Brannan card:
 * - layer: an absolute 100vw background layer (same breakout as PageOrScreenWidthImage)
 * - container: padding on the band that reserves the art's space, so a late-loading
 *   PNG can never shift the h1 (the LCP element)
 */
export const getMastheadLayerCss = (accent: ResolvedMasthead): StyleSystemProps => {
  const { masthead, assetUrl, mobileAssetUrl } = accent;
  return {
    // Half-res asset below sm2 (art paints at half width there); must stay in lockstep
    // with the media-scoped preload links in GuestSiteHead.
    backgroundImage: { _: `url(${mobileAssetUrl})`, sm2: `url(${assetUrl})` },
    backgroundRepeat: 'no-repeat',
    // Fixed painted size per breakpoint (catalog width on desktop, half below sm2); on
    // viewports narrower than the art, the sides crop around the centered name.
    backgroundSize: { _: `${masthead.width / 2}px auto`, sm2: `${masthead.width}px auto` },
    backgroundPosition: masthead.placement === 'top' ? 'center top' : 'center center',
  };
};

export const getMastheadContainerCss = (accent: ResolvedMasthead): StyleSystemProps => {
  if (accent.masthead.placement === 'top') {
    return {
      marginTop: [0, 0, 'unset'],
      marginBottom: [40, 40, 'unset'],
      paddingTop: TOP_ORNAMENT_PADDING_TOP,
    };
  }
  return {
    marginTop: [0, 0, 'unset'],
    marginBottom: [40, 40, 'unset'],
    paddingY: CENTER_FRAME_PADDING_Y,
  };
};
